import { ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { ProjectDto } from '@models/project-dto.interface';
import { ProjectsService } from 'src/app/services/projects.service';
import { EditProjectComponent, EditProjectDialogData } from '../edit-project/edit-project.component';
import { MatIconModule } from '@angular/material/icon';
import { AttachmentService } from 'src/app/services/attachment.service';
import { AttachmentType } from 'src/app/enums/attachment-type.enum';
import { CommentsComponent } from '@shared/components/comments/comments.component';
import { ProjectNotesProvider } from 'src/app/services/project-notes-provider.service';

@Component({
  selector: 'app-project-details',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule, MatButtonModule, MatDialogModule, MatIconModule, CommentsComponent],
  templateUrl: './project-details.component.html',
  styleUrl: './project-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectDetailsComponent implements OnChanges, OnDestroy {
  @Input() projectId: number | null = null;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private readonly projectsService = inject(ProjectsService);
  private readonly dialog = inject(MatDialog);
  private readonly attachmentService = inject(AttachmentService);
  protected readonly projectNotesProvider = inject(ProjectNotesProvider);
  private projectRequest?: Subscription;
  private lastLoadedProjectId: number | null = null;

  protected project = signal<ProjectDto | null>(null);
  protected isLoading = signal(false);
  protected errorMessage = signal('');

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['projectId']) {
      return;
    }

    if (this.projectId == null) {
      this.resetState();
      return;
    }

    if (this.projectId === this.lastLoadedProjectId && this.project() !== null && this.errorMessage() === '') {
      return;
    }

    this.loadProject(this.projectId);
  }

  ngOnDestroy(): void {
    this.projectRequest?.unsubscribe();
  }

  protected billingLabel(): string {
    return this.project()?.isTurnKey ? 'Turn Key' : 'PAG';
  }

  protected displayValue(value: string | null | undefined): string {
    return value && value.trim().length > 0 ? value : 'N/A';
  }

  protected openEditDialog(): void {
    if (this.projectId == null) return;

    const dialogData: EditProjectDialogData = { mode: 'edit', projectId: this.projectId };
    const ref = this.dialog.open(EditProjectComponent, {
      data: dialogData,
      disableClose: true,
      autoFocus: true,
      width: '740px',
      maxWidth: '90vw',
    });

    ref.afterClosed().subscribe((saved: boolean) => {
      if (saved && this.projectId != null) {
        this.lastLoadedProjectId = null;
        this.loadProject(this.projectId);
        this.projectsService.triggerReset(false);
      }
    });
  }

  protected onUploadAttachment(): void {
    if (this.projectId == null) return;
    this.fileInput.nativeElement.click();
  }

  protected onFileSelected(event: Event): void {
    if (this.projectId == null) return;

    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    this.attachmentService.upload(this.projectId, AttachmentType.Project, file).subscribe({
      next: () => {
        input.value = '';
      },
      error: (err) => {
        console.error('Project attachment upload failed', err);
        input.value = '';
      }
    });
  }

  private loadProject(projectId: number): void {
    this.projectRequest?.unsubscribe();
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.projectRequest = this.projectsService.getProject(projectId).subscribe({
      next: (project) => {
        this.project.set(project);
        this.lastLoadedProjectId = projectId;
        this.isLoading.set(false);
      },
      error: () => {
        this.project.set(null);
        this.lastLoadedProjectId = null;
        this.errorMessage.set('Unable to load project details.');
        this.isLoading.set(false);
      }
    });
  }

  private resetState(): void {
    this.projectRequest?.unsubscribe();
    this.project.set(null);
    this.lastLoadedProjectId = null;
    this.errorMessage.set('');
    this.isLoading.set(false);
  }
}